<script setup lang="ts">
/**
 * Transfer 문서 내 [[변수]] 자리 — OtcDocVarField 미러링(축소판).
 *   · date   → 인라인 편집 + 📅
 *   · person → 인라인 편집 + ✨ (챗봇 person lookup)
 *   · address→ 인라인 편집 + 📍
 *   · text   → 인라인 직접 편집
 *   · derived→ 읽기전용(합계/자동매핑)
 */
import { computed, inject, ref, onMounted, watch, nextTick } from 'vue';
import { TRANSFER_DOC_CTX } from './transferContext';

const props = defineProps<{ fieldKey: string; placeholder?: string }>();
const ctx = inject(TRANSFER_DOC_CTX)!;

const storeKey = computed(() => props.fieldKey);
const value = computed(() => ctx.valueOf(storeKey.value));
const label = computed(() => props.placeholder || ctx.labelOf(props.fieldKey));
const fieldType = computed(() => ctx.typeOf(props.fieldKey));
const isDate = computed(() => fieldType.value === 'date');
const isAddress = computed(() => fieldType.value === 'address');
const isDerived = computed(() => ctx.isDerived(props.fieldKey));
const showIcon = computed(() => ['person', 'date', 'address'].includes(fieldType.value));
const active = computed(() => ctx.activeKey.value === storeKey.value);
const invalid = computed(() => (ctx.isInvalid ? ctx.isInvalid(storeKey.value) : false));

function onIcon() {
  if (isAddress.value) return; // address 는 인라인 편집만(주소모달은 witness 주소 등 별도 미사용)
  ctx.activate(storeKey.value);
}
const iconName = computed(() => (isDate.value ? 'mdi-calendar-month' : isAddress.value ? 'mdi-map-marker-outline' : 'mdi-creation'));
const iconClass = computed(() => (isDate.value ? 'ic-date' : isAddress.value ? 'ic-addr' : 'ic-ai'));
const iconTip = computed(() => (isDate.value ? 'Pick a date' : isAddress.value ? 'Enter address' : 'Look up from chat'));

// ── 인라인 편집 ──
const ed = ref<HTMLElement>();
onMounted(() => { if (ed.value) ed.value.textContent = value.value; });
watch(value, async (v) => {
  await nextTick();
  if (ed.value && document.activeElement !== ed.value && ed.value.textContent !== v) ed.value.textContent = v;
});
function onInput() {
  if (!ed.value) return;
  const txt = ed.value.textContent || '';
  if (!txt && ed.value.innerHTML) ed.value.innerHTML = '';
  ctx.setFieldValue(storeKey.value, txt);
}
function clearField() {
  ctx.setFieldValue(storeKey.value, '');
  if (ed.value) ed.value.innerHTML = '';
}

// ── 드래그앤드롭 ──
const dragOver = ref(false);
function onDrop(e: DragEvent) {
  dragOver.value = false;
  let v = '';
  const json = e.dataTransfer?.getData('application/x-entity-field');
  if (json) { try { v = String(JSON.parse(json).value ?? ''); } catch { /* noop */ } }
  if (!v) v = e.dataTransfer?.getData('text/plain') || '';
  if (v) ctx.setFieldValue(storeKey.value, v.trim());
}
</script>

<template>
  <!-- 파생 읽기전용 -->
  <span
    v-if="isDerived"
    :data-transfer-field="storeKey"
    class="trf trf-derived" :class="{ empty: !value, filled: !!value }"
    :title="'Auto-mapped from the transfer information entered above'"
  >
    <span class="trf-in trf-ro" :data-ph="label">{{ value }}</span>
  </span>

  <span
    v-else
    :data-transfer-field="storeKey"
    class="trf" :class="{ empty: !value, filled: !!value, active, dragover: dragOver, invalid }"
    @dragover.prevent="dragOver = true" @dragleave="dragOver = false" @drop.prevent="onDrop"
  >
    <span
      ref="ed" class="trf-in" :data-ph="label" contenteditable="true" spellcheck="false"
      @input="onInput" @keydown.enter.prevent="ed?.blur()"
    />
    <button
      v-if="showIcon"
      class="trf-ic" :class="iconClass" :title="iconTip"
      @mousedown.prevent @click.stop="onIcon"
    >
      <v-icon size="12">{{ iconName }}</v-icon>
    </button>
    <button v-if="!!value" class="trf-clear" title="Clear" @mousedown.prevent @click.stop="clearField">
      <v-icon size="11">mdi-close</v-icon>
    </button>
  </span>
</template>

<style scoped>
.trf { display:inline-flex; align-items:center; gap:3px; border-radius:4px; padding:0 3px; vertical-align:baseline; }
.trf.empty { background:#fff6d6; box-shadow:inset 0 -2px 0 #f2c94c; }
.trf.filled { background:#eaf3ff; box-shadow:inset 0 -2px 0 #1B84FF; }
.trf.invalid { background:#fdecec; box-shadow:inset 0 -2px 0 #e5484d; }
.trf.active { outline:2px solid #1B84FF; outline-offset:1px; }
.trf.dragover { outline:2px dashed #22a06b; background:#e6f7ee; }
.trf-in { min-width:46px; outline:none; font:inherit; color:#10305c; white-space:pre-wrap; }
.trf.filled .trf-in { font-weight:600; }
.trf-in:empty::before { content:attr(data-ph); color:#9a7b1a; font-style:italic; font-size:.9em; cursor:text; }
.trf-derived { cursor:default; background:transparent; }
.trf-ro, .trf-ro:empty::before { cursor:default; }
.trf-derived.filled .trf-ro { font-weight:600; }
.trf-ic { display:inline-flex; align-items:center; justify-content:center; width:18px; height:18px; border-radius:50%; border:none; cursor:pointer; flex:0 0 auto; padding:0; }
.trf-ic.ic-ai { background:#eaf3ff; color:#1B84FF; }
.trf-ic.ic-date { background:#eafaf1; color:#22a06b; }
.trf-ic.ic-addr { background:#fdeefe; color:#a23bb0; }
.trf-ic:hover { filter:brightness(.94); }
.trf-clear { display:none; align-items:center; justify-content:center; width:15px; height:15px; border-radius:50%; border:none; background:#c4ccd6; color:#fff; cursor:pointer; flex:0 0 auto; padding:0; }
.trf:hover .trf-clear { display:inline-flex; }
.trf-clear:hover { background:#8b95a1; }
</style>
